import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { LEAD_STATUSES, LEAD_STATUS_LABELS } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { PreviewLeadPicker } from '@/features/emailTemplates/PreviewLeadPicker';
import { EmailTemplatePreviewPane } from '@/features/emailTemplates/EmailTemplatePreviewPane';
import styles from './sequenceEditor.module.css';
import projectStyles from '@/pages/ProjectsPage.module.css';

type SimStep = {
  step_order: number;
  channel: string;
  subject: string;
  body_text: string;
  body_html?: string | null;
  skipped?: boolean;
  warnings?: string[];
};

type Props = {
  projectId: string;
  sequenceId: string;
};

export function SequenceSimulatePanel({ projectId, sequenceId }: Props) {
  const [leadId, setLeadId] = useState('');
  const [assumedTags, setAssumedTags] = useState('');
  const [assumedStatus, setAssumedStatus] = useState('contacted');

  const simulate = useMutation({
    mutationFn: () =>
      apiFetch<{
        simulation: SimStep[];
        branch_scenario: {
          path_step_orders: number[];
          next_step_after: Record<string, number>;
          assumed_tags: string[];
          assumed_status: string;
        };
      }>(`/api/sequences/${sequenceId}/simulate`, {
        method: 'POST',
        body: JSON.stringify({
          lead_id: leadId,
          assumed_tags: assumedTags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
          assumed_status: assumedStatus || undefined,
        }),
      }),
  });

  const firstEmail = simulate.data?.simulation.find((s) => s.channel === 'email' && !s.skipped);

  return (
    <div className={projectStyles.formGrid}>
      <PreviewLeadPicker projectId={projectId} value={leadId} onChange={setLeadId} />
      <Field label="Aangenomen tags (komma)">
        <TextInput value={assumedTags} onChange={(e) => setAssumedTags(e.target.value)} placeholder="vip, warm" />
      </Field>
      <Field label="Aangenomen leadstatus">
        <select
          className={styles.sequenceListBtn}
          style={{ padding: '8px 12px' }}
          value={assumedStatus}
          onChange={(e) => setAssumedStatus(e.target.value)}
        >
          {LEAD_STATUSES.map((s) => (
            <option key={s} value={s}>
              {LEAD_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </Field>
      <Button type="button" variant="secondary" disabled={!leadId || simulate.isPending} onClick={() => simulate.mutate()}>
        {simulate.isPending ? 'Simuleren…' : 'Simuleer pad + mail'}
      </Button>

      {simulate.data?.branch_scenario ? (
        <p className={styles.simPath}>
          Pad: stap {simulate.data.branch_scenario.path_step_orders.join(' → ')}
          {Object.entries(simulate.data.branch_scenario.next_step_after).map(([from, to]) => (
            <span key={from}>
              {' '}
              · na {from} → {to}
            </span>
          ))}
        </p>
      ) : null}

      {simulate.data?.simulation?.length ? (
        <div className={projectStyles.mutedSmall}>
          {simulate.data.simulation.map((s) => (
            <details key={s.step_order} style={{ marginBottom: '0.75rem' }}>
              <summary>
                Stap {s.step_order} ({s.channel})
                {s.skipped ? ' — overgeslagen' : `: ${s.subject}`}
              </summary>
              {(s.warnings ?? []).map((w) => (
                <p key={w}>{w}</p>
              ))}
              {!s.skipped ? <pre style={{ whiteSpace: 'pre-wrap' }}>{s.body_text}</pre> : null}
            </details>
          ))}
        </div>
      ) : null}

      {firstEmail && leadId ? (
        <EmailTemplatePreviewPane
          projectId={projectId}
          subject={firstEmail.subject}
          blocks={[]}
          bodyText={firstEmail.body_text}
          bodyHtml={firstEmail.body_html ?? undefined}
          renderMode="text"
          leadId={leadId}
          presentation="inbox"
        />
      ) : null}
    </div>
  );
}
